import {
  Box,
  Button,
  IconButton,
  Theme,
  Typography,
} from "@outschool/backpack";
import { faBars } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { LearnerRoutes } from "@outschool/routes";
import { OUTSCHOOL_TIMEZONE, dayjs } from "@outschool/time";
import { ReadOnlyBanner } from "@outschool/ui-components-website";
import { useLinkComponent } from "@outschool/ui-utils";
import React from "react";

import * as Env from "../../Env";
import { useCurrentLearner } from "../../providers/CurrentLearnerProvider";
import { useLearnerClassroomContext } from "../../providers/LearnerClassroomProvider";
import { useLessonsNavDrawer } from "../../providers/LessonsNavDrawerProvider";

export default function ClassroomHeaderV2() {
  const Link = useLinkComponent();
  const { t } = useTranslation("learnerApp\\LearnerClassroom");
  const currentLearner = useCurrentLearner();
  const { openNavDrawer } = useLessonsNavDrawer();
  const { loading, error, activity, section } = useLearnerClassroomContext();

  if (loading || error || !activity || !section) {
    return null;
  }

  const userTimeZone = currentLearner?.timeZone || OUTSCHOOL_TIMEZONE;
  const endTime = section.end_time
    ? dayjs(section.end_time).tz(userTimeZone)
    : null;

  return (
    <Box
      data-test-id="class-header-v2"
      sx={(theme: Theme) => ({
        paddingX: 72,
        paddingY: 22,
        [theme.breakpoints.down("md")]: {
          padding: 0,
        },
      })}
    >
      {Env.IS_READ_ONLY_MODE && <ReadOnlyBanner />}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "8px",
        }}
      >
        <Link
          to={LearnerRoutes.dashboardPath()}
          style={{ textDecoration: "none" }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              color: "grey.900",
              letterSpacing: "0.1px",
            }}
          >
            {t("Back")}
          </Typography>
        </Link>

        <Box
          sx={(theme: Theme) => ({
            display: "block",
            [theme.breakpoints.up("md")]: {
              display: "none",
            },
          })}
        >
          <IconButton
            icon={faBars}
            variant="text"
            onClick={openNavDrawer}
            aria-label={t("Open lessons menu")}
          />
        </Box>
      </Box>

      <Box
        sx={(theme: Theme) => ({
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          [theme.breakpoints.down("md")]: {
            flexDirection: "column",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: "1.6rem",
          },
        })}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "7px",
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              letterSpacing: "-0.4px",
              color: "grey.900",
            }}
          >
            {activity.title}
          </Typography>

          {endTime && (
            <Typography
              variant="body1"
              sx={{
                marginBottom: "10px",
                color: "grey.600",
              }}
            >
              {t("Class access ends {{date}}", {
                date: endTime.format("MMM D, YYYY"),
              })}
            </Typography>
          )}
        </Box>

        <Button
          variant="contained"
          sx={{
            whiteSpace: "nowrap",
            borderRadius: "9999px",
            textTransform: "none",
            fontSize: 18,
            fontWeight: 500,
            width: 200,
            height: 48,
            boxShadow: "0px 0px 2px #6161F2",
          }}
          onClick={() => {
            // TODO: Add logic to open the new messaging feature once ready
          }}
        >
          {t("Message teacher")}
        </Button>
      </Box>
    </Box>
  );
}
